import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router';
import { useNotificationContext } from '@/Context/NotificationContext';
import useCompanyId from '@/Hooks/UseCompanyId';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';
import { useEditRecruitmentContext } from '../../Edit/Contexts/EditRecruitmentContext';
import StepProps from '../StepProps';
import JobAdCompanyDescription from './Components/JobAdCompanyDescription';
import JobAdExplanation from './Components/JobAdExplanation';
import JobAdStepHeader from './Components/JobAdStepHeader';
import JobAdStepNavigation from './Components/JobAdStepNavigation';
import JobAdTitle from './Components/JobAdTitle';
import JobDescription from './Components/JobDescription';
import RequiredSkills from './Components/RequiredSkills';
import { JobAdFormValues, jobAdFormValues2Model, jobAdModel2FormValues, jobAdValidationSchema } from './JobAdStepUtils';

const EditJobAdStep = ({ activeStep, handleBack, handleNext }: StepProps): React.JSX.Element => {
    const navigate = useNavigate();
    const companyId = useCompanyId();
    const { jobAd, setJobAd, jobDetails, client, updateRecruitmentWithoutExperienceCriteriaAsync, processingState } =
        useEditRecruitmentContext();
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const form = useForm({
        mode: 'all',
        // @ts-ignore buggy yup needs to be updated
        resolver: yupResolver(jobAdValidationSchema),
        defaultValues: jobAdModel2FormValues(jobAd),
    });

    const onSubmit = async (values: JobAdFormValues) => {
        const { status: currentStatus } = jobAd;

        if (handleNext !== undefined) {
            setJobAd(jobAdFormValues2Model(values));
            handleNext();
        } else {
            try {
                await updateRecruitmentWithoutExperienceCriteriaAsync({ ...values, status: currentStatus });
                showSuccessToaster(UIStrings.RecruitmentUpdated);

                // navigate to client recruitment overview.
                const createRecruitmentPath = generatePath(RoutePath.AssessmentsActive, {
                    companyId,
                    clientId: client.id,
                });
                navigate(createRecruitmentPath);
            } catch {
                showFailToaster();
            }
        }
    };

    const handleBackWrapper = () => {
        const values = form.getValues();
        setJobAd(jobAdFormValues2Model(values));

        handleBack?.();
    };

    return (
        <div className="mx-auto w-full max-w-4xl self-center">
            <JobAdStepHeader />
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="border-border bg-alicia flex flex-col gap-7 rounded-lg border px-5 py-8">
                        <JobAdExplanation />
                        <JobAdTitle />
                        <RequiredSkills />
                        <JobDescription initialHtml={jobAd.description} processingState={processingState} />
                        <JobAdCompanyDescription
                            initialHtml={jobAd.companyDescription}
                            processingState={processingState}
                        />
                    </div>
                    <JobAdStepNavigation
                        activeStep={activeStep}
                        handleBack={handleBackWrapper}
                        processingState={processingState}
                        jobAd={jobAd}
                        setJobAd={setJobAd}
                        jobDetails={jobDetails}
                        nextButtonLabel={handleNext === undefined ? UIStrings.Update : UIStrings.Next}
                    />
                </form>
            </FormProvider>
        </div>
    );
};

export default EditJobAdStep;
